import type { Vec3, Waypoint } from '@/content/schema';

export interface Pose {
  position: Vec3;
  lookAt: Vec3;
}

const smoothstep = (t: number) => t * t * (3 - 2 * t);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerp3 = (a: Vec3, b: Vec3, t: number): Vec3 => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
  lerp(a[2], b[2], t),
];

export function poseAt(waypoints: readonly Waypoint[], p: number): Pose {
  if (waypoints.length === 0) throw new Error('poseAt: no waypoints');
  const first = waypoints[0]!;
  const last = waypoints[waypoints.length - 1]!;
  if (p <= first.at) return { position: first.position, lookAt: first.lookAt };
  if (p >= last.at) return { position: last.position, lookAt: last.lookAt };
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i]!;
    const b = waypoints[i + 1]!;
    if (p >= a.at && p <= b.at) {
      const span = b.at - a.at;
      const t = span <= 0 ? 1 : smoothstep((p - a.at) / span);
      return { position: lerp3(a.position, b.position, t), lookAt: lerp3(a.lookAt, b.lookAt, t) };
    }
  }
  return { position: last.position, lookAt: last.lookAt };
}

export function flattenWaypoints(
  stations: readonly { waypoints: readonly Waypoint[] }[],
): Waypoint[] {
  return stations.flatMap((s) => s.waypoints).sort((a, b) => a.at - b.at);
}
